import { useEffect, useState } from "react";
import { ArrowRight, BarChart3, ChevronDown, FileText, Package, Play, Scale, ShieldCheck, ShoppingBag, Sparkles, Store, Truck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import AuthDialog from "../components/AuthDialog";
import { getPageUrl } from "./legal/content";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const HERO_IMG = "https://images.unsplash.com/flagged/photo-1553617569-8ef7a8da3146?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FAQ_GROUPS = [
    ["platform", "Platform"],
    ["supplier", "Tedarikçiler"],
    ["store", "Mağaza"],
];

const FEATURES = [
    { icon: Scale, title: "Tedarikçi Karşılaştırma", desc: "Aynı ürünün fiyatını, MOQ'unu ve teslim süresini tedarikçiler arasında yan yana görün." },
    { icon: FileText, title: "Teklif (RFQ) Yönetimi", desc: "Toplu alım talebinizi tüm tedarikçilere tek tıkla gönderin, teklifleri tabloda kıyaslayın." },
    { icon: Package, title: "Akıllı Sepet & Bölünmüş Sipariş", desc: "Sepetiniz tedarikçi bazında otomatik bölünür; her biri kendi sipariş, sevkiyat ve faturasıyla ilerler." },
    { icon: Truck, title: "Sevkiyat & Teslimat Takibi", desc: "Kargo durumu, tahmini teslim ve teslim alan bilgisi tek ekranda." },
    { icon: BarChart3, title: "Satınalma Analitiği", desc: "Aylık harcama, kategori dağılımı, tedarikçi performansı ve tasarruf raporları." },
    { icon: Sparkles, title: "AI Satınalma Asistanı", desc: "Canlı platform verisiyle çalışan yapay zekâ: en iyi fiyatı ve doğru tedarikçiyi bulur." },
];

function VideoCard({ video, index }) {
    const [play, setPlay] = useState(false);
    return (
        <div className="overflow-hidden rounded-2xl border border-stone-800 bg-stone-900/40" data-testid={`video-card-${index}`}>
            <div className="relative aspect-video">
                {play ? (
                    <iframe
                        src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1`}
                        title={video.title}
                        className="absolute inset-0 h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid={`video-iframe-${index}`}
                    />
                ) : (
                    <button onClick={() => setPlay(true)} className="group absolute inset-0 h-full w-full" data-testid={`video-play-${index}`} aria-label={`${video.title} oynat`}>
                        <img src={`https://i.ytimg.com/vi/${video.youtube_id}/hqdefault.jpg`} alt={video.title} className="h-full w-full object-cover" loading="lazy" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/20">
                            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-terra-600 text-white shadow-lg transition-transform group-hover:scale-110">
                                <Play className="h-6 w-6 fill-current" />
                            </span>
                        </span>
                    </button>
                )}
            </div>
            <p className="px-4 py-3 text-sm font-semibold text-stone-200">{video.title}</p>
        </div>
    );
}

export default function Landing() {
    const { user, loading } = useAuth();
    const [categories, setCategories] = useState([]);
    const [pages, setPages] = useState([]);
    const [content, setContent] = useState({});
    const [regType, setRegType] = useState(null);

    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        if (p.get("giris") === "1") setAuthOpen(true);
        const kayit = p.get("kayit");
        if (kayit === "tedarikci") setRegType("supplier");
        if (kayit === "magaza") setRegType("buyer");
    }, []);
    const emptyForm = { company_name: "", contact_name: "", email: "", phone: "", city: "", details: "", note: "" };
    const [regForm, setRegForm] = useState(emptyForm);
    const [regCats, setRegCats] = useState([]);
    const [regSending, setRegSending] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);
    const [faqTab, setFaqTab] = useState("platform");
    const [faqOpen, setFaqOpen] = useState(false);
    const [videos, setVideos] = useState([]);
    const [authOpen, setAuthOpen] = useState(false);

    const openReg = (type) => {
        setRegForm(emptyForm);
        setRegCats([]);
        setRegType(type);
    };

    const submitReg = async () => {
        if (!regForm.company_name.trim() || !regForm.email.trim() || !regForm.phone.trim()) {
            return toast.error("Firma adı, e-posta ve telefon zorunludur");
        }
        setRegSending(true);
        try {
            await api.post("/registrations", { type: regType, ...regForm, categories: regCats });
            toast.success("Başvurunuz alındı! Ekibimiz en kısa sürede sizinle iletişime geçecek.");
            setRegType(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Başvuru gönderilemedi");
        } finally {
            setRegSending(false);
        }
    };

    const toggleCat = (cid) => setRegCats((p) => (p.includes(cid) ? p.filter((x) => x !== cid) : [...p, cid]));

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
        api.get("/pages").then((r) => setPages(r.data)).catch(() => {});
        api.get("/content").then((r) => setContent(r.data)).catch(() => {});
        api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    }, []);

    const c = (key, fallback) => content[key] || fallback;
    const faqs = Object.keys(content)
        .filter((k) => /^faq_\d+_q$/.test(k))
        .sort((a, b) => parseInt(a.match(/\d+/)[0]) - parseInt(b.match(/\d+/)[0]))
        .map((k) => ({ q: content[k], a: content[k.replace(/_q$/, "_a")], c: content[k.replace(/_q$/, "_c")] || "platform" }))
        .filter((f) => f.q && f.a);
    const filteredFaqs = faqs.filter((f) => f.c === faqTab);

    if (!loading && user) {
        // giriş yapmış kullanıcılar da siteyi gezebilir — üstte "Panele Git" butonu gösterilir
    }

    return (
        <div className="min-h-screen bg-stone-950 text-stone-100" data-testid="landing-page">
            <div className="grain-overlay pointer-events-none fixed inset-0 z-10" />
            <header className="relative z-20 border-b border-stone-800/60">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
                    <div className="flex items-center gap-2.5">
                        <img src={content.site_logo_icon || "/logo-icon.png"} alt="ATA Angora" className="h-9 w-9 object-contain" data-testid="landing-logo" />
                        <span className="font-display text-xl font-bold tracking-tight">
                            <span className="text-terra-500">ata</span> <span className="text-emerald-400">angora</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-2" data-testid="landing-nav-menu">
                        {user ? (
                            <Link
                                to="/panel"
                                className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-stone-900 transition-transform hover:scale-[1.03]"
                                data-testid="landing-panel-button"
                            >
                                Panele Git
                            </Link>
                        ) : (
                            <>
                                <button
                                    onClick={() => openReg("supplier")}
                                    className="hidden rounded-full border border-terra-500/50 px-4 py-2 text-xs font-semibold text-terra-500 transition-colors hover:bg-terra-600/20 sm:block"
                                    data-testid="landing-register-supplier"
                                >
                                    Tedarikçi Kaydı
                                </button>
                                <button
                                    onClick={() => openReg("store")}
                                    className="hidden rounded-full border border-emerald-500/50 px-4 py-2 text-xs font-semibold text-emerald-400 transition-colors hover:bg-emerald-600/20 sm:block"
                                    data-testid="landing-register-store"
                                >
                                    Mağaza Kaydı
                                </button>
                                <button
                                    onClick={() => setAuthOpen(true)}
                                    className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-stone-900 transition-transform hover:scale-[1.03]"
                                    data-testid="landing-login-button"
                                >
                                    Giriş Yap
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} />

            <section className="relative z-0 overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/85 to-stone-950/40" />
                </div>
                <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32">
                    <div className="max-w-2xl">
                        <div className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-terra-500/40 bg-terra-600/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-terra-500">
                            <ShieldCheck className="h-3.5 w-3.5" /> {c("hero_badge", "B2B Satınalma Platformu")}
                        </div>
                        <h1 className="animate-fade-up-1 mt-6 font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                            {c("hero_title_1", "Anadolu'nun tadını")}
                            <span className="block text-terra-500">{c("hero_title_2", "tek platformdan")}</span>
                            {c("hero_title_3", "tedarik edin.")}
                        </h1>
                        <p className="animate-fade-up-2 mt-6 max-w-xl text-base leading-relaxed text-stone-300 sm:text-lg">
                            {c("hero_subtitle", "Aktar, restoran, market ve HORECA işletmeleri için: ürün arayın, tedarikçileri karşılaştırın, teklif alın, siparişten teslimata tüm süreci yönetin.")}
                        </p>
                        <div className="animate-fade-up-3 mt-8 flex flex-wrap items-center gap-4">
                            <button
                                onClick={() => setAuthOpen(true)}
                                className="group inline-flex items-center gap-2 rounded-full bg-terra-600 px-7 py-3.5 font-semibold text-white shadow-lg shadow-terra-600/30 transition-all hover:bg-terra-700 hover:shadow-terra-600/50"
                                data-testid="hero-google-login"
                            >
                                <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" opacity=".9"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" opacity=".7"/><path fill="currentColor" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" opacity=".5"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" opacity=".8"/></svg>
                                Google ile Başla
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </button>
                            <div className="flex items-center gap-6 text-xs text-stone-400">
                                <span>{c("stat_1", "8+ doğrulanmış tedarikçi")}</span>
                                <span>{c("stat_2", "30+ ürün")}</span>
                                <span>{c("stat_3", "56 kategori")}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative z-20 border-t border-stone-800/60 bg-stone-900/40 py-16">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <h2 className="font-display text-2xl font-bold sm:text-3xl">{c("categories_title", "Kategoriler")}</h2>
                    <p className="mt-2 text-sm text-stone-400">{c("categories_subtitle", "Baharatından kuruyemişine, bakliyatından zeytinyağına.")}</p>
                    <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                        {categories.filter((cat) => !cat.parent_id).map((c) => {
                            const subCount = categories.filter((s) => s.parent_id === c.category_id).length;
                            return (
                                <Link key={c.category_id} to={`/kategori/${c.slug}`} className="group relative overflow-hidden rounded-2xl border border-stone-800 text-left transition-colors hover:border-terra-500/60" data-testid={`landing-category-${c.slug}`}>
                                    <img src={c.image} alt={c.name} loading="lazy" decoding="async" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/20 to-transparent" />
                                    {subCount > 0 && <span className="absolute right-2 top-2 rounded-full bg-stone-950/80 px-2 py-0.5 text-[10px] font-semibold text-stone-300">{subCount} alt</span>}
                                    <div className="absolute bottom-0 p-3">
                                        <p className="font-display text-sm font-bold leading-tight">{c.name}</p>
                                        <p className="text-[10px] text-stone-400">{c.product_count} ürün</p>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="relative z-20 py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <div className="max-w-xl">
                        <h2 className="font-display text-2xl font-bold sm:text-3xl">Satınalma süreciniz, uçtan uca.</h2>
                        <p className="mt-3 text-sm leading-relaxed text-stone-400">
                            Ürün ara → Tedarikçi karşılaştır → Teklif al → Sepet → Sipariş → Sevkiyat → Teslimat → Fatura → Rapor.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {FEATURES.map((f, i) => (
                            <div key={f.title} className="rounded-2xl border border-stone-800 bg-stone-900/50 p-6 transition-colors hover:border-terra-500/40" data-testid={`feature-${f.title.replace(/\s/g, "-")}`}>
                                <f.icon className="h-8 w-8 text-terra-500" />
                                <h3 className="mt-4 font-display text-lg font-bold">{c(`feature_${i + 1}_title`, f.title)}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-stone-400">{c(`feature_${i + 1}_desc`, f.desc)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {videos.length > 0 && (
                <section id="videolar" className="relative z-20 border-t border-stone-800/60 py-20" data-testid="videos-section">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terra-500">Videolar</p>
                        <h2 className="mt-2 font-display text-3xl font-extrabold sm:text-4xl">{c("videos_title", "Platformu İzleyin")}</h2>
                        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {videos.map((v, i) => (
                                <VideoCard key={v.video_id} video={v} index={i} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="relative z-20 border-t border-stone-800/60 bg-gradient-to-b from-stone-900/60 to-stone-950 py-20" data-testid="join-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <div className="max-w-xl">
                        <p className="text-xs font-semibold uppercase tracking-widest text-saffron-400">{c("join_badge", "Platforma Katılın")}</p>
                        <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl">{c("join_title", "Tedarikçi veya aktar mağazası olarak katılın.")}</h2>
                        <p className="mt-3 text-sm leading-relaxed text-stone-400">
                            {c("join_subtitle", "Binlerce işletmeye ulaşın. Başvurunuzu bırakın, ekibimiz doğrulama sonrası hesabınızı aktifleştirsin.")}
                        </p>
                    </div>
                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        <div className="rounded-2xl border border-terra-500/30 bg-stone-900/60 p-8 transition-colors hover:border-terra-500/60" data-testid="join-supplier-card">
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-terra-600/20">
                                <Store className="h-6 w-6 text-terra-500" />
                            </div>
                            <h3 className="mt-5 font-display text-xl font-bold">{c("join_supplier_title", "Tedarikçi Kaydı")}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-stone-400">
                                {c("join_supplier_desc", "Baharat, kuruyemiş, bakliyat ve gıda ürünlerinizi binlerce alıcı işletmeye listeleyin. Tekliflere katılın, sipariş ve sevkiyatınızı panelden yönetin.")}
                            </p>
                            <ul className="mt-4 space-y-1.5 text-xs text-stone-400">
                                <li>• Komisyonsuz başlangıç dönemi</li>
                                <li>• RFQ / toplu alım taleplerine teklif verme</li>
                                <li>• Stok ve envanter yönetim paneli</li>
                            </ul>
                            <button
                                onClick={() => openReg("supplier")}
                                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-terra-600 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-terra-700"
                                data-testid="join-supplier-button"
                            >
                                Tedarikçi Başvurusu <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </button>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-stone-900/60 p-8 transition-colors hover:border-emerald-500/60" data-testid="join-store-card">
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-600/20">
                                <ShoppingBag className="h-6 w-6 text-emerald-400" />
                            </div>
                            <h3 className="mt-5 font-display text-xl font-bold">{c("join_store_title", "Aktar Mağaza Kaydı")}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-stone-400">
                                {c("join_store_desc", "Aktar, kuruyemişçi veya doğal ürünler mağazanızı platforma kaydedin. Binlerce ürüne toptan fiyatla ulaşın, teklif isteyin.")}
                            </p>
                            <ul className="mt-4 space-y-1.5 text-xs text-stone-400">
                                <li>• Doğrulanmış tedarikçilerden toptan alım</li>
                                <li>• RFQ ile toplu teklif alma</li>
                                <li>• Sipariş, sevkiyat ve fatura takibi</li>
                            </ul>
                            <button
                                onClick={() => openReg("store")}
                                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-700 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-emerald-800"
                                data-testid="join-store-button"
                            >
                                Mağaza Başvurusu <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {faqs.length > 0 && (
                <section id="sss" className="relative z-20 border-t border-stone-800/60" data-testid="faq-section">
                    <div className="mx-auto max-w-3xl px-4 sm:px-6">
                        <button
                            onClick={() => setFaqOpen((v) => !v)}
                            className="flex w-full items-center justify-between gap-4 py-6 text-left"
                            data-testid="faq-toggle"
                        >
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terra-500">SSS</p>
                                <h2 className="mt-1 font-display text-2xl font-extrabold sm:text-3xl">Sık Sorulan Sorular</h2>
                            </div>
                            <ChevronDown className={`h-6 w-6 shrink-0 text-terra-500 transition-transform ${faqOpen ? "rotate-180" : ""}`} />
                        </button>
                        {faqOpen && (
                            <div className="pb-14">
                                <div className="flex flex-wrap gap-2" data-testid="faq-groups">
                                    {FAQ_GROUPS.map(([val, label]) => (
                                        <button
                                            key={val}
                                            onClick={() => { setFaqTab(val); setOpenFaq(null); }}
                                            className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                                                faqTab === val ? "border-terra-500 bg-terra-600 text-white" : "border-stone-700 text-stone-400 hover:border-terra-500/50 hover:text-white"
                                            }`}
                                            data-testid={`faq-group-${val}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div className="mt-6 space-y-3">
                                    {filteredFaqs.map((f, i) => (
                                        <div key={i} className="overflow-hidden rounded-xl border border-stone-800 bg-stone-900/40" data-testid={`faq-item-${i}`}>
                                            <button
                                                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-stone-900/70"
                                                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                                data-testid={`faq-question-${i}`}
                                            >
                                                <span className="text-sm font-semibold sm:text-base">{f.q}</span>
                                                <ChevronDown className={`h-4 w-4 shrink-0 text-terra-500 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                                            </button>
                                            {openFaq === i && (
                                                <p className="border-t border-stone-800/60 px-5 py-4 text-sm leading-relaxed text-stone-400" data-testid={`faq-answer-${i}`}>{f.a}</p>
                                            )}
                                        </div>
                                    ))}
                                    {filteredFaqs.length === 0 && (
                                        <p className="text-sm text-stone-500" data-testid="faq-empty-group">Bu bölümde henüz soru bulunmuyor.</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <footer className="relative z-20 border-t border-stone-800/60 py-12" data-testid="landing-footer">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
                        <div className="md:col-span-4">
                            <div className="flex items-center gap-2">
                                <img src={content.site_logo_icon || "/logo-icon.png"} alt="" className="h-7 w-7 object-contain" />
                                <span className="font-display text-base font-bold"><span className="text-terra-500">ata</span> <span className="text-emerald-400">angora</span></span>
                            </div>
                            <p className="mt-3 max-w-xs text-xs leading-relaxed text-stone-400">
                                {c("footer_about", "Aktar, baharat, kuruyemiş ve gıda sektörü için uçtan uca B2B satınalma platformu.")}
                            </p>
                        </div>
                        <div className="md:col-span-3">
                            <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">Platform</p>
                            <ul className="mt-3 space-y-2 text-sm">
                                <li><button onClick={() => openReg("supplier")} className="text-stone-300 transition-colors hover:text-terra-500" data-testid="footer-supplier-reg">Tedarikçi Kaydı</button></li>
                                <li><Link to="/tedarikciler" className="text-stone-300 transition-colors hover:text-terra-500" data-testid="footer-suppliers">Tedarikçiler</Link></li>
                                <li><button onClick={() => openReg("store")} className="text-stone-300 transition-colors hover:text-emerald-400" data-testid="footer-store-reg">Aktar Mağaza Kaydı</button></li>
                                <li><button onClick={() => { setFaqOpen(true); scrollToSection("sss"); }} className="text-stone-300 transition-colors hover:text-white" data-testid="footer-faq">SSS</button></li>
                                <li><button onClick={() => scrollToSection("videolar")} className="text-stone-300 transition-colors hover:text-white" data-testid="footer-videos">Videolar</button></li>
                                <li><button onClick={() => setAuthOpen(true)} className="text-stone-300 transition-colors hover:text-white" data-testid="footer-login">Giriş Yap</button></li>
                            </ul>
                        </div>
                        <div className="md:col-span-3">
                            <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">Yasal</p>
                            <ul className="mt-3 space-y-2 text-sm" data-testid="footer-legal-links">
                                {pages.map((p) => (
                                    <li key={p.page_id}>
                                        <Link to={getPageUrl(p)} className="text-stone-300 transition-colors hover:text-white" data-testid={`footer-page-${p.slug}`}>{p.title}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="md:col-span-2">
                            <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">Destek</p>
                            <ul className="mt-3 space-y-2 text-sm">
                                <li><Link to="/iletisim" className="text-stone-300 transition-colors hover:text-white" data-testid="footer-iletisim">İletişim</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-stone-800/60 pt-6">
                        <p className="text-xs text-stone-500">© 2026 ATA Angora Tedarik Platformu — Ankara. Tüm hakları saklıdır.</p>
                        <p className="text-[10px] uppercase tracking-widest text-stone-600">Güvenli B2B Tedarik</p>
                    </div>
                </div>
            </footer>

            <Dialog open={!!regType} onOpenChange={() => setRegType(null)}>
                <DialogContent className="max-w-lg" data-testid="registration-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            {regType === "supplier" ? "Tedarikçi Kayıt Başvurusu" : "Aktar Mağaza Kayıt Başvurusu"}
                        </DialogTitle>
                        <DialogDescription>
                            Başvurunuz platform ekibi tarafından incelenir, onay sonrası hesabınız aktifleştirilir.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Input placeholder="Firma Adı *" value={regForm.company_name} onChange={(e) => setRegForm({ ...regForm, company_name: e.target.value })} data-testid="reg-company-name" />
                        <Input placeholder="Yetkili Kişi" value={regForm.contact_name} onChange={(e) => setRegForm({ ...regForm, contact_name: e.target.value })} data-testid="reg-contact-name" />
                        <Input type="email" placeholder="E-posta *" value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} data-testid="reg-email" />
                        <Input placeholder="Telefon *" value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} data-testid="reg-phone" />
                        <Input placeholder="Şehir" value={regForm.city} onChange={(e) => setRegForm({ ...regForm, city: e.target.value })} data-testid="reg-city" />
                        <Input
                            placeholder={regType === "supplier" ? "Ana ürün grubu (örn: kuruyemiş)" : "İşletme tipi (örn: aktar, kuruyemişçi, restoran)"}
                            value={regForm.details}
                            onChange={(e) => setRegForm({ ...regForm, details: e.target.value })}
                            data-testid="reg-details"
                        />
                    </div>
                    {categories.length > 0 && (
                        <div data-testid="reg-categories">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{regType === "supplier" ? "Ürün Kategorileri" : "İlgilendiğiniz Kategoriler"}</p>
                            <div className="flex flex-wrap gap-2">
                                {categories.filter((c) => !c.parent_id).map((c) => (
                                    <button
                                        key={c.category_id}
                                        type="button"
                                        onClick={() => toggleCat(c.category_id)}
                                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${regCats.includes(c.category_id) ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`}
                                        data-testid={`reg-cat-${c.slug}`}
                                    >
                                        {c.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <Textarea placeholder="Ek not (opsiyonel)" rows={3} value={regForm.note} onChange={(e) => setRegForm({ ...regForm, note: e.target.value })} data-testid="reg-note" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRegType(null)}>Vazgeç</Button>
                        <Button onClick={submitReg} disabled={regSending} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="reg-submit">
                            Başvuruyu Gönder
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
